// 文件名: src/components/TD1502.js
import React, { useState, useMemo } from 'react';
import { ArrowRight, Maximize, Target, Move } from 'lucide-react';
import { useTranslation } from '../i18n';
import { calculateTopDownCoverage } from '../utils/TopDownCalc';
import PersonHeightControl from './common/PersonHeightControl';
import { useUnit } from '../contexts/UnitContext';
import { formatLength, getDisplayValue, getMetersFromInput, getUnitLabel, getAreaUnitLabel } from '../utils/unitConversion';

export const LENS_PARAMS_TD1502 = {
    '2.2mm': { halfWidth1m: 1.4000, halfHeight1m: 0.7875 },
    '3.6mm': { halfWidth1m: 0.7650, halfHeight1m: 0.4303 },
    '6.0mm': { halfWidth1m: 0.4200, halfHeight1m: 0.2363 },
    '12mm': { halfWidth1m: 0.2121, halfHeight1m: 0.1212 }, // Assuming linear dropoff further down
    '16mm': { halfWidth1m: 0.1585, halfHeight1m: 0.0906 },
};

export const HEIGHT_RANGES_TD1502 = {
    '2.2mm': [2.3, 5.5],
    '3.6mm': [3.0, 9.0],
    '6.0mm': [4.5, 15.0],
    '12mm': [10.0, 30.0],
    '16mm': [15.0, 40.0],
};

export const calculateTd1502Coverage = (inputs) => {
    const { installationHeight, lensType = '2.2mm', personHeight = 1.75, xTilt = 0, yTilt = 0 } = inputs;
    const lens = LENS_PARAMS_TD1502[lensType] || LENS_PARAMS_TD1502['2.2mm'];

    const result = calculateTopDownCoverage({
        height: Math.max(0.1, installationHeight),
        personHeight,
        xTiltDeg: xTilt,
        yTiltDeg: yTilt,
        halfWidth1m: lens.halfWidth1m,
        halfHeight1m: lens.halfHeight1m
    });

    if (!result.isValid || !result.coverageField) {
        return { error: 'Invalid calculation range or angle too steep' };
    }

    return { 
        error: null, 
        viewWidth: result.coverageField.aveWidth, 
        viewDepth: result.coverageField.aveHeight,
        area: result.coverageField.area,
        details: result
    };
};

const Controls = ({ inputs, setInputs, isAdmin }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    const { installationHeight, lensType, xTilt = 0, yTilt = 0, personHeight = 1.75 } = inputs;

    const minHeight = HEIGHT_RANGES_TD1502[lensType][0];
    const maxHeight = HEIGHT_RANGES_TD1502[lensType][1];
    
    const displayMinHeight = getDisplayValue(minHeight, unitSystem);
    const displayMaxHeight = getDisplayValue(maxHeight, unitSystem);

    const handleInputChange = (field, value, isFloat = true) => {
        setInputs(prev => ({ ...prev, [field]: isFloat ? parseFloat(value) : value }));
    };
    
    const handleMeterInputChange = (field, displayValue) => {
        setInputs(prev => ({ ...prev, [field]: getMetersFromInput(parseFloat(displayValue), unitSystem) }));
    };

    const handleLensChange = (e) => {
        const newLensType = e.target.value;
        const newRange = HEIGHT_RANGES_TD1502[newLensType];
        let currentHeight = installationHeight;
        if (currentHeight < newRange[0]) currentHeight = newRange[0];
        else if (currentHeight > newRange[1]) currentHeight = newRange[1];

        setInputs(prev => ({
            ...prev,
            lensType: newLensType,
            installationHeight: currentHeight
        }));
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('controlsTitle')}</h2>
            <div className="space-y-6">
                <div>
                    <label htmlFor="lensType" className="block text-sm font-medium text-gray-600 mb-1">{t('lensModel')}</label>
                    <select id="lensType" value={lensType} onChange={handleLensChange}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500">
                        {Object.keys(LENS_PARAMS_TD1502).map(lens => <option key={lens} value={lens}>{lens}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="installationHeight" className="block text-sm font-medium text-gray-600 mb-1">
                        {t('installationHeight')}: <span className="font-bold text-cyan-600">{formatLength(installationHeight, unitSystem, 1)}</span>
                        <span className="text-xs ml-2 text-gray-500">({t('range')}: {formatLength(minHeight, unitSystem, 1)} - {formatLength(maxHeight, unitSystem, 1)})</span>
                    </label>
                    <input type="range" id="installationHeight"
                        min={displayMinHeight} max={displayMaxHeight} step={unitSystem === 'imperial' ? "0.5" : "0.1"} value={getDisplayValue(installationHeight, unitSystem)}
                        onChange={(e) => handleMeterInputChange('installationHeight', e.target.value)}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                            {t('xTilt')}: <span className="font-bold text-cyan-600">{xTilt} °</span>
                        </label>
                        <input type="range" min="-15" max="15" step="1" value={xTilt}
                            onChange={(e) => handleInputChange('xTilt', e.target.value, false)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                            {t('yTilt')}: <span className="font-bold text-cyan-600">{yTilt} °</span>
                        </label>
                        <input type="range" min="-15" max="15" step="1" value={yTilt}
                            onChange={(e) => handleInputChange('yTilt', e.target.value, false)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                    </div>
                </div>
                <div className="pt-4 border-t border-gray-100">
                    <PersonHeightControl
                        personHeight={personHeight}
                        onPersonHeightInput={(newHeight) => handleInputChange('personHeight', newHeight)}
                        onPersonHeightChange={(newHeight) => handleInputChange('personHeight', newHeight)}
                        isAdmin={isAdmin}
                    />
                </div>
            </div>
        </div>
    );
};

const SummaryResults = ({ data }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!data || data.error) return null;
    const { viewWidth, viewDepth, area, details } = data;

    const ResultCard = ({ title, value, unit, icon }) => (
        <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-4">
            {icon}
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-xl font-bold text-gray-800">{value} <span className="text-sm font-normal">{unit}</span></p>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('effectiveRange')} (3D Top-View Matrix)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ResultCard title={t('effectiveWidth')} value={getDisplayValue(viewWidth, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} icon={<Maximize className="w-8 h-8 text-blue-500" />} />
                <ResultCard title={t('effectiveDepth')} value={getDisplayValue(viewDepth, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} icon={<ArrowRight className="w-8 h-8 text-red-500 transform rotate-90" />} />
                <ResultCard title={t('coverageArea')} value={getDisplayValue(area, unitSystem).toFixed(2)} unit={getAreaUnitLabel(unitSystem)} icon={<Target className="w-8 h-8 text-purple-500" />} />
                <ResultCard title="Bounds Offset" value={`${getDisplayValue(details?.coverageField?.width - viewWidth, unitSystem).toFixed(2)}`} unit={`${getUnitLabel(unitSystem)} skew`} icon={<Move className="w-8 h-8 text-orange-500" />} />
            </div>
        </div>
    );
};

const SideViewDiagram = ({ data, inputs }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!data || data.error || !data.details) return null;
    
    const { installationHeight, yTilt = 0 } = inputs;
    const vf = data.details.visionField;
    const cf = data.details.coverageField;
    if (!vf || !cf) return null;

    const width = 500, height = 350, padding = 40;
    
    // Project Y coordinates for Side View (Y-Z plane)
    const yCoordsCoverage = cf.polygon.map(p => p.y);
    const yCoordsVision = vf.polygon.map(p => p.y);
    const minY = Math.min(...yCoordsCoverage);
    const maxY = Math.max(...yCoordsCoverage);
    const visionMinY = Math.min(...yCoordsVision);
    const visionMaxY = Math.max(...yCoordsVision);
    
    const maxAbsY = Math.max(Math.abs(visionMinY), Math.abs(visionMaxY));
    const horizontalSpan = maxAbsY * 2;
    
    const scaleX = horizontalSpan > 0 ? (width - padding * 2) / horizontalSpan : 1;
    const scaleY = installationHeight > 0 ? (height - padding * 2.5) / installationHeight : 1;
    const scale = Math.min(scaleX, scaleY);
    
    const groundY = height - padding * 2;
    const camX = width / 2;
    const camY = groundY - installationHeight * scale;
    
    const visionStartPx = camX + visionMinY * scale;
    const visionEndPx = camX + visionMaxY * scale;
    const coverageStartPx = camX + minY * scale;
    const coverageEndPx = camX + maxY * scale;

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('sideView')} (Y-Z)</h2>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                <defs>
                    <marker id="arrowhead-td1502-side" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" /></marker>
                </defs>
                <line x1="0" y1={groundY} x2={width} y2={groundY} stroke="#9ca3af" strokeWidth="1" />
                <text x={width - 5} y={groundY - 5} fontSize="10" fill="#6b7280" textAnchor="end">{t('ground')}</text>
                
                <line x1={camX} y1={camY} x2={camX} y2={groundY} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" />
                <text x={camX + 5} y={camY} fontSize="10" fill="#6b7280">H: {formatLength(installationHeight, unitSystem, 1)}</text>
                
                <polygon points={`${camX},${camY} ${visionEndPx},${groundY} ${visionStartPx},${groundY}`} fill="rgba(0, 180, 255, 0.1)" />
                <line x1={camX} y1={camY} x2={visionEndPx} y2={groundY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                <line x1={camX} y1={camY} x2={visionStartPx} y2={groundY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                
                <rect x={coverageStartPx} y={groundY - 5} width={coverageEndPx - coverageStartPx} height="10" fill="rgba(22, 163, 74, 0.7)" />
                
                <g transform={`translate(${camX}, ${camY}) rotate(${yTilt})`}>
                    <rect x="-10" y="-6" width="20" height="12" rx="2" fill="#0891b2" />
                    <circle cx="0" cy="0" r="4" fill="white" />
                </g>
                
                <line x1={coverageStartPx} y1={groundY + 20} x2={coverageEndPx} y2={groundY + 20} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-td1502-side)" markerEnd="url(#arrowhead-td1502-side)"/>
                <text x={coverageStartPx + (coverageEndPx - coverageStartPx)/2} y={groundY + 33} fontSize="10" fill="#3b82f6" textAnchor="middle">{t('effectiveDepth')}: {formatLength(maxY-minY, unitSystem, 2)}</text>
            </svg>
        </div>
    );
};

const TopDownDiagram = ({ data }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!data || data.error || !data.details) return null;
    
    const vf = data.details.visionField;
    const cf = data.details.coverageField;
    if (!vf || !cf) return null;

    const width = 500, height = 400, padding = 40;
    
    const allPoints = [...vf.polygon, ...cf.polygon, {x:0, y:0}];
    const xCoords = allPoints.map(p => p.x);
    const yCoords = allPoints.map(p => p.y);
    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);
    
    const dataWidth = maxX - minX;
    const dataHeight = maxY - minY;
    const dataCenterX = (minX + maxX) / 2;
    const dataCenterY = (minY + maxY) / 2;
    
    const scale = Math.min( dataWidth > 0 ? (width - padding * 2) / dataWidth : Infinity, dataHeight > 0 ? (height - padding * 2) / dataHeight : Infinity );
    const toSvgCoord = (p) => ({ x: (width / 2) + (p.x - dataCenterX) * scale, y: (height / 2) - (p.y - dataCenterY) * scale, });
    
    const visionPolyPoints = vf.polygon.map(toSvgCoord).map(p => `${p.x},${p.y}`).join(' ');
    const coveragePolyPoints = cf.polygon.map(toSvgCoord).map(p => `${p.x},${p.y}`).join(' ');
    
    const visionBbox = { 
        minX: Math.min(...vf.polygon.map(p => toSvgCoord(p).x)), 
        maxX: Math.max(...vf.polygon.map(p => toSvgCoord(p).x)), 
        minY: Math.min(...vf.polygon.map(p => toSvgCoord(p).y)), 
        maxY: Math.max(...vf.polygon.map(p => toSvgCoord(p).y))
    };
    
    const coverageBbox = { 
        minX: Math.min(...cf.polygon.map(p => toSvgCoord(p).x)), 
        maxX: Math.max(...cf.polygon.map(p => toSvgCoord(p).x)), 
        minY: Math.min(...cf.polygon.map(p => toSvgCoord(p).y)), 
        maxY: Math.max(...cf.polygon.map(p => toSvgCoord(p).y))
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('topDownView')} (X-Y)</h2>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                 <defs><marker id="arrowhead-td1502-top" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" /></marker></defs>
                
                <polygon points={visionPolyPoints} fill="rgba(0, 180, 255, 0.1)" stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                <polygon points={coveragePolyPoints} fill="rgba(22, 163, 74, 0.2)" stroke="rgba(22, 163, 74, 0.8)" strokeWidth="1.5" />
                
                <line x1={visionBbox.minX} y1={visionBbox.maxY - 10} x2={visionBbox.maxX} y2={visionBbox.maxY - 10} stroke="rgba(0, 180, 255, 0.7)" strokeWidth="1" markerStart="url(#arrowhead-td1502-top)" markerEnd="url(#arrowhead-td1502-top)"/>
                <text x={(visionBbox.minX + visionBbox.maxX) / 2} y={visionBbox.maxY - 15} fontSize="10" fill="rgba(0, 180, 255, 1)" textAnchor="middle">{t('widthLabel')}: {formatLength(vf.aveWidth, unitSystem, 2)}</text>
                
                <line x1={visionBbox.maxX + 10} y1={visionBbox.minY} x2={visionBbox.maxX + 10} y2={visionBbox.maxY} stroke="rgba(0, 180, 255, 0.7)" strokeWidth="1" markerStart="url(#arrowhead-td1502-top)" markerEnd="url(#arrowhead-td1502-top)"/>
                <text x={visionBbox.maxX + 15} y={(visionBbox.minY + visionBbox.maxY) / 2} fontSize="10" fill="rgba(0, 180, 255, 1)" textAnchor="start" dominantBaseline="middle">{t('depthLabel')}: {formatLength(vf.aveHeight, unitSystem, 2)}</text>
                
                <line x1={coverageBbox.minX} y1={coverageBbox.minY + 10} x2={coverageBbox.maxX} y2={coverageBbox.minY + 10} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-td1502-top)" markerEnd="url(#arrowhead-td1502-top)"/>
                <text x={(coverageBbox.minX + coverageBbox.maxX) / 2} y={coverageBbox.minY + 22} fontSize="10" fill="#3b82f6" textAnchor="middle">{t('effectiveWidth')}: {formatLength(cf.aveWidth, unitSystem, 2)}</text>
                
                <line x1={coverageBbox.minX - 10} y1={coverageBbox.minY} x2={coverageBbox.minX - 10} y2={coverageBbox.maxY} stroke="#ef4444" strokeWidth="1" markerStart="url(#arrowhead-td1502-top)" markerEnd="url(#arrowhead-td1502-top)"/>
                <text x={coverageBbox.minX - 15} y={(coverageBbox.minY + coverageBbox.maxY) / 2} fontSize="10" fill="#ef4444" textAnchor="end" dominantBaseline="middle">{t('effectiveDepth')}: {formatLength(cf.aveHeight, unitSystem, 2)}</text>
                
                <circle cx={toSvgCoord({x:0,y:0}).x} cy={toSvgCoord({x:0,y:0}).y} r="5" fill="#0891b2" />
                <text x={toSvgCoord({x:0,y:0}).x} y={toSvgCoord({x:0,y:0}).y + 15} fontSize="10" textAnchor="middle">{t('cameraPosition')}</text>
                
                <text x={10} y={20} fontSize="12" fill="rgba(0, 180, 255, 1)">{t('viewingRange')}</text>
                <text x={10} y={35} fontSize="12" fill="rgba(22, 163, 74, 1)">{t('countingRange')}</text>
            </svg>
        </div>
    );
};

export default function TD1502Calculator({ isAdmin }) {
    const [inputs, setInputs] = useState({
        installationHeight: 3.5,
        lensType: '2.2mm',
        personHeight: 1.75,
        xTilt: 0,
        yTilt: 0
    });

    const calculationResults = useMemo(() => calculateTd1502Coverage(inputs), [inputs]);

    if (isAdmin) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-8">
                    <Controls inputs={inputs} setInputs={setInputs} isAdmin={isAdmin} />
                </div>
                <div className="lg:col-span-2 space-y-8">
                    {calculationResults.error ? (
                        <div className="p-4 bg-red-100 text-red-700 rounded-lg">{calculationResults.error}</div>
                    ) : (
                        <>
                            <SummaryResults data={calculationResults} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <SideViewDiagram data={calculationResults} inputs={inputs} />
                                <TopDownDiagram data={calculationResults} />
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    } else {
        return (
            <div className="flex flex-col gap-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1">
                        <Controls inputs={inputs} setInputs={setInputs} isAdmin={isAdmin} />
                    </div>
                    <div className="lg:col-span-2">
                        {calculationResults.error ? (
                             <div className="p-4 bg-red-100 text-red-700 rounded-lg">{calculationResults.error}</div>
                        ) : (
                            <SummaryResults data={calculationResults} />
                        )}
                    </div>
                </div>
                {!calculationResults.error && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <SideViewDiagram data={calculationResults} inputs={inputs} />
                        <TopDownDiagram data={calculationResults} />
                    </div>
                )}
            </div>
        );
    }
}
// 功能描述: TD1502型号客流摄像机覆盖范围计算器（重构为支持全向倾斜的 3D Top-Down 投影算法）
